<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import type { SearchResponse } from 'meilisearch'
import type { ICreditCard } from '~/components/entity/credit'
import { EntityCreditItem, EntityCreditItemSkeleton } from '~/components/entity/credit'
import { FeaturePagination } from '~/components/feature/pagination'
import { WidgetCreditCalculatorFilters } from './filters'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { LucideNewspaper } from 'lucide-vue-next'

const props = defineProps<{
  // Enables the category-level highlight flag (`highlight_in_categories`)
  isCategoryPage?: boolean
}>()

const config = useRuntimeConfig()
const { locale } = useI18n()
const currencyList = useCurrencyStore().list

// Filter state — defaults match WidgetCreditCalculatorFilters defineModel defaults
const amount = ref<number | undefined>(500000)
const currency = ref<string>('kgs')
const period = ref<string>('12')
const forWhom = ref<string>('private_person')
const organizationType = ref<string[]>([])
const incomeConfirmation = ref<string>('any')
const guarantee = ref<string>('any')
const collateral = ref<string>('any')
const initialFee = ref<string>('any')
const downPayment = ref<number | undefined>(undefined)

const debouncedAmount = useDebounce(amount, 500)
const currentPage = ref(1)

// Annuity monthly payment: P = S * r / (1 - (1+r)^-n)
const calculateMonthlyPayment = (loanAmount: number, annualRate: number, months: number): number => {
  if (annualRate === 0) return Math.round(loanAmount / months)
  const r = annualRate / 100 / 12
  return Math.round(loanAmount * r / (1 - Math.pow(1 + r, -months)))
}

// Build MeiliSearch filter string from current filter state
const activeFilter = computed(() => {
  const filters: string[] = []

  if (debouncedAmount.value && currency.value && currency.value !== 'any') {
    const amt = debouncedAmount.value
    const baseCondition = `(settings.min_amount_${currency.value} <= ${amt} AND (settings.max_amount_${currency.value} >= ${amt} OR settings.max_amount_${currency.value} = 0))`
    filters.push(`(${baseCondition})`)
  }

  if (period.value && period.value !== '0') {
    let periodFilter = `(rates.period >= ${period.value})`
    if (currency.value && currency.value !== 'any') {
      const curId = currencyList.find((el) => el.code.toLowerCase() === currency.value)?.id
      if (curId) {
        periodFilter = `(${periodFilter} AND rates.currency_id = ${curId})`
      }
    }
    filters.push(periodFilter)
  }

  if (forWhom.value && forWhom.value !== '0') {
    filters.push(`for_whom.${forWhom.value} = true`)
  }

  if (organizationType.value.length === 1) {
    filters.push(`organization.type = "${organizationType.value[0]}"`)
  } else if (organizationType.value.length > 1) {
    const orgConditions = organizationType.value
      .map((t) => `organization.type = "${t}"`)
      .join(' OR ')
    filters.push(`(${orgConditions})`)
  }

  if (incomeConfirmation.value && incomeConfirmation.value !== 'any') {
    filters.push(`income_confirmation = "${incomeConfirmation.value === '1'}"`)
  }

  if (guarantee.value && guarantee.value !== 'any') {
    filters.push(`guarantee_required = "${guarantee.value === '1'}"`)
  }

  if (collateral.value && collateral.value !== 'any') {
    filters.push(`collateral = "${collateral.value === '1'}"`)
  }

  if (initialFee.value && initialFee.value !== 'any') {
    filters.push(`initial_fee_required = "${initialFee.value === '1'}"`)
  }

  if (currency.value && currency.value !== 'any') {
    filters.push(`settings.is_${currency.value} = true`)
  }

  // Hardcoded: mortgage purpose only
  filters.push(`(purposes.id = 9)`)

  return [`(${filters.join(' AND ')})`]
})

const { search: searchCredits } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_credits`)

// When a specific currency is selected, use that currency's values instead of the general ones
const activeCurrency = computed(() =>
  currency.value && currency.value !== 'any' ? currency.value : undefined
)

const { settingKey, localizedSetting, numericSetting } = useMeiliCurrencySettings(activeCurrency)

const {
  data: result,
  pending,
  error,
  refresh
} = await useAsyncData<SearchResponse<ICreditCard> | null>('mortgage-calculator', async () => {
  // Sort by the selected currency's rate value, falling back to the general one
  const minRateValueSort = `settings.${settingKey('min_rate_value')}:asc`
  // Category-highlighted credits go right after the globally highlighted ones
  const highlightSort = props.isCategoryPage
    ? ['highlight:desc', 'highlight_in_categories:desc']
    : ['highlight:desc']

  try {
    return (await searchCredits('', {
      hitsPerPage: 20,
      page: currentPage.value,
      sort: ['weight:desc', ...highlightSort, minRateValueSort, 'updated_at:desc'],
      filter: activeFilter.value
    })) as unknown as SearchResponse<ICreditCard>
  } catch (e) {
    console.error('MeiliSearch mortgage calculator error:', e)
    return null
  }
})

// Transform raw MeiliSearch hits to ICreditCard shape with localized strings
const hits = computed(() => {
  const minRateValueKey = settingKey('min_rate_value')
  const minRateKey = settingKey('min_rate')
  const maxPeriodKey = settingKey('max_period')

  return (result.value?.hits || []).map((hit) => {
    const loanAmount = (debouncedAmount.value ?? 0) - (downPayment.value ?? 0)
    const months = period.value !== '0' ? parseInt(period.value) : 0
    const rate = numericSetting(hit.settings, minRateValueKey)
    const currencyId = currencyList.find((c) => c.code.toLowerCase() === currency.value)?.id ?? 1
    const monthlyPayment =
      loanAmount > 0 && months > 0
        ? { amount: calculateMonthlyPayment(loanAmount, rate, months), currencyId }
        : undefined

    const isCategoryHighlight = props.isCategoryPage && hit.highlight_in_categories
    const isHighlighted = hit.highlight || isCategoryHighlight

    return {
      id: hit.id,
      url: hit.url,
      title: hit.title?.[locale.value] || '',
      min_rate: localizedSetting(hit.settings, minRateKey) ?? '',
      max_period: localizedSetting(hit.settings, maxPeriodKey) ?? null,
      types: (hit.types || []).map((type) => ({
        ...type,
        title: type.title?.[locale.value] || ''
      })),
      currencies: hit.currencies,
      organization: {
        ...hit.organization,
        title: hit.organization.title?.[locale.value] || '',
        official_title: hit.organization.official_title?.[locale.value] || ''
      },
      highlight: isHighlighted,
      is_islamic: hit.is_islamic,
      monthlyPayment
    }
  })
})

const changePage = (page: number) => {
  currentPage.value = page
  refresh()
}

// Reset to page 1 and re-fetch when any filter changes
watch(amount, () => {
  downPayment.value = undefined
})

watch(
  [debouncedAmount, currency, period, forWhom, organizationType, incomeConfirmation, guarantee, collateral, initialFee],
  () => {
    currentPage.value = 1
    refresh()
  }
)
</script>

<template>
  <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
    <!-- Filters column -->
    <div class="lg:col-span-1">
      <WidgetCreditCalculatorFilters
        v-model:amount="amount"
        v-model:currency="currency"
        v-model:period="period"
        v-model:for-whom="forWhom"
        v-model:organization-type="organizationType"
        v-model:income-confirmation="incomeConfirmation"
        v-model:guarantee="guarantee"
        v-model:collateral="collateral"
        v-model:initial-fee="initialFee"
        v-model:down-payment="downPayment"
      />
    </div>

    <!-- Results column -->
    <div class="lg:col-span-2">
      <EntityCreditItemSkeleton v-if="pending" :count="5" />

      <template v-else-if="!error && result">
        <div v-if="hits.length" class="CardsList">
          <EntityCreditItem
            v-for="item in hits"
            :key="item.id"
            :item="item"
            :hide-compare="false"
            :selected-currency="activeCurrency"
          />
          <div class="mt-7 flex justify-end">
            <FeaturePagination
              :meta="{
                current_page: result.page || 1,
                total: result.totalHits || 0,
                per_page: result.hitsPerPage || 0,
                last_page: result.totalPages || 0
              }"
              :links="{
                prev: currentPage > 1 ? `?page=${currentPage - 1}` : null,
                next: currentPage < (result.totalPages || 1) ? `?page=${currentPage + 1}` : null
              }"
              @change="changePage"
            />
          </div>
        </div>

        <Alert v-else variant="blue">
          <LucideNewspaper />
          <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
        </Alert>
      </template>

      <Alert v-else-if="error" variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </div>
  </div>
</template>
